import PropTypes from 'prop-types';

import { confirm, IconCellRenderer } from '@trueprodigy/components';

import AgGrid from '@components/AgGrid/AgGrid';

import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import styled from 'styled-components';

import { fetchingState } from '@constants';

import { showError, showSuccess } from '@actions/global';
import * as ownerActions from '@modules/appraisal/actions/owner';
import * as ownerSelectors from '@modules/appraisal/selectors/owner';
import { propertySelector } from '@modules/appraisal/selectors/property';

import {
  InfoBlock,
  FormGroupTitleContainer,
  FormGroupTitle,
} from '@modules/appraisal/components/subjectDetailForm/formComponents';

import CustomSelect from '@components/CustomSelect';

const buildColumnDefs = (dispatch, canEdit) => [
  {
    headerName: 'Tag',
    field: 'tag',
  },
  { headerName: 'Create Date', field: 'createDt' },
  {
    headerName: 'Entered By',
    field: 'createdBy',
  },
  {
    headerName: 'Delete',
    field: 'delete',
    cellRenderer: IconCellRenderer,
    cellRendererParams: {
      icons: [
        {
          icon: canEdit && 'fa-trash',
          onClick: async (_, data) => {
            try {
              await confirm('owner tag');
              await dispatch(
                ownerActions.deleteOwnerTag({
                  tagID: data.tagID,
                }),
              );
              await dispatch(
                ownerActions.fetchOwnerTags({
                  ownerID: data.ownerID,
                }),
              );
              dispatch(showSuccess('Owner Tag Deleted'));
            } catch (error) {
              dispatch(showError());
            }
          },
        },
      ],
    },
  },
];

const OwnerTags = ({ ownerID, isReadOnly }) => {
  const dispatch = useDispatch();

  const property = useSelector(propertySelector);
  const codefileOwnerTags = useSelector(
    ownerSelectors.getCodefileOwnerTags,
  ).toJS();
  const ownerTagsData = useSelector(ownerSelectors.ownerTagsSelector);
  const ownerTags = ownerTagsData.get('data')
    ? ownerTagsData.get('data').toJS()
    : [];
  const ownerTagsState = ownerTagsData.get('state');
  const [codifiedTag, setCodifiedTag] = useState('');

  const [gridApi, setGridApi] = useState(null);

  const certified =
    property.getIn(['data', 'rollCorrGroupStatus']) === 'Certified';

  useEffect(() => {
    dispatch(ownerActions.fetchCodefileOwnerTags());
    dispatch(ownerActions.fetchOwnerTags({ ownerID }));
  }, []);

  useEffect(() => {
    if (gridApi) {
      if (ownerTagsState === fetchingState.FETCHING) {
        gridApi.showLoadingOverlay();
      } else if (
        ownerTags.length === 0 ||
        ownerTagsState === fetchingState.ERROR
      ) {
        gridApi.showNoRowsOverlay();
      } else {
        gridApi.hideOverlay();
      }
    }
  }, [ownerTags, ownerTagsState]);

  const handleAddPropertyTag = async tag => {
    try {
      await dispatch(
        ownerActions.insertOwnerTag({
          ownerID,
          data: { tag },
        }),
      );
      await dispatch(ownerActions.fetchOwnerTags({ ownerID }));
      setCodifiedTag(null);
      dispatch(showSuccess('Owner Tag Added'));
    } catch (error) {
      dispatch(showError());
    }
  };

  const onGridReady = ({ api }) => {
    api.sizeColumnsToFit();
    setGridApi(api);
  };

  return (
    <OwnerTags.Wrapper>
      <OwnerTags.Container>
        <InfoBlock width="100%">
          <FormGroupTitleContainer>
            <FormGroupTitle>Tags</FormGroupTitle>
            <CustomSelect
              handleLarge
              disabled={certified || isReadOnly}
              styles={{ width: '100%', marginBottom: 20 }}
              input={{
                value: codifiedTag,
                onChange: ({ value }) => {
                  setCodifiedTag(value);
                  handleAddPropertyTag(value);
                },
              }}
              placeholder="Add a Codified Tag"
              name="codifiedTag"
              options={
                codefileOwnerTags.length > 0
                  ? codefileOwnerTags.map(({ codeName }) => ({
                      value: codeName,
                      label: codeName,
                    }))
                  : []
              }
            />

            <OwnerTags.GridWrapper className="ag-theme-material">
              <AgGrid
                columnDefs={buildColumnDefs(
                  dispatch,
                  !certified && !isReadOnly,
                )}
                defaultColDef={{ resizable: true, sortable: true }}
                onGridReady={onGridReady}
                rowData={ownerTags}
                disablePreferenceSave
              />
            </OwnerTags.GridWrapper>
          </FormGroupTitleContainer>
        </InfoBlock>
      </OwnerTags.Container>
    </OwnerTags.Wrapper>
  );
};

OwnerTags.propTypes = {
  ownerID: PropTypes.string,
  isReadOnly: PropTypes.any,
};

OwnerTags.Wrapper = styled.div`
  padding-top: 0;
  background-color: #eee;
`;

OwnerTags.Container = styled.div`
  background-color: #fff;
  padding: 11px 36px 20px 16px;
  filter: none;
`;

OwnerTags.SummaryGrouper = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 14px;
`;

OwnerTags.Summary = styled.h3`
  text-transform: uppercase;
  font-size: 28px;
  font-weight: bold;
  color: #212121;
  margin: 0;
`;

OwnerTags.GridWrapper = styled.div`
  height: calc(100vh / 2);
  width: 100%;
`;

export default OwnerTags;
